/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { VNode } from 'preact';
import {
  HTMLAttributes,
  useCallback,
  useEffect,
  useState,
  Fragment,
} from 'preact/compat';
import { Container, Slot, SlotProps } from '@adobe-commerce/elsie/lib';
import { events } from '@adobe-commerce/event-bus';
import { CartModel, AppliedGiftCardProps } from '@/cart/data/models';
import { getPersistedCartData } from '@/cart/lib/persisted-data';
import { useEstimatedTotals } from '@/cart/hooks';
import { state } from '@/cart/lib/state';
import { Button, Price, Icon } from '@adobe-commerce/elsie/components';
import { useText, Text } from '@adobe-commerce/elsie/i18n';
import {
  OrderSummary as OrderSummaryComponent,
  OrderSummaryLineItem,
} from '@/cart/components';
import { publishInitiateCheckoutEvent } from '@/cart/lib/acdl';
import { Coupon, GiftCard } from '@adobe-commerce/elsie/icons';

// Delay navigation to allow analytics event to be sent before page unload.
// 100ms provides a reasonable balance between user experience and event reliability.
const ANALYTICS_NAVIGATION_DELAY_MS = 100;

interface CheckoutRouteContext {
  cartId: string;
}

export interface OrderSummaryProps extends HTMLAttributes<HTMLDivElement> {
  routeCheckout?: (context: CheckoutRouteContext) => string;
  slots?: {
    EstimateShipping?: SlotProps;
    Coupons?: SlotProps;
    GiftCards?: SlotProps;
  };
  enableCoupons?: boolean;
  enableGiftCards?: boolean;
  errors: boolean;
  showTotalSaved?: boolean;
  updateLineItems?: (
    lineItems: Array<OrderSummaryLineItem>
  ) => Array<OrderSummaryLineItem>;
}

export const OrderSummary: Container<OrderSummaryProps, CartModel | null> = ({
  children,
  initialData = null,
  routeCheckout,
  slots,
  errors,
  showTotalSaved,
  enableCoupons,
  enableGiftCards,
  updateLineItems = (lineItems) => lineItems,
  ...props
}) => {
  const [data, setData] = useState<CartModel | null>(initialData);
  const [hasQuantityErrors, setHasQuantityErrors] = useState(false);
  const isVirtual = data?.isVirtual;

  enableCoupons = enableCoupons ?? true;
  enableGiftCards = enableGiftCards ?? true;

  const {
    handleEstimateTotals,
    estimatedTotals,
    setEstimatedTotals,
    loading: handleEstimateLoading,
  } = useEstimatedTotals();

  const cartTaxesConfig = state.config?.shoppingCartDisplaySetting;
  const isIncludingTax = cartTaxesConfig?.subtotal === 'INCLUDING_TAX';
  const isIncludingAndExcludingTax =
    cartTaxesConfig?.subtotal === 'INCLUDING_EXCLUDING_TAX';
  const isZeroTax = cartTaxesConfig?.zeroTax;
  const calculateTotalGiftCardAmount = () => {
    return (
      data?.appliedGiftCards.reduce((acc: number, current: any) => {
        return acc + current.appliedBalance.value;
      }, 0) ?? 0
    );
  };
  const appliedGiftCardsPriceConfig = (giftCard: AppliedGiftCardProps) => {
    if (!giftCard?.code) return null;

    return (
      <Fragment key={giftCard.code}>
        <span className="cart-order-summary__coupon__code">
          <Icon source={GiftCard} size="16" />
          <span>{giftCard?.code}</span>
        </span>
        <span className="cart-order-summary__caption">
          <Text id="Cart.PriceSummary.giftCard.appliedGiftCards.remainingBalance" />
          <Price
            className="cart-order-summary__caption"
            weight="normal"
            size="small"
            amount={giftCard.giftCardBalance.value}
            currency={giftCard.giftCardBalance.currency}
          />
        </span>
      </Fragment>
    );
  };
  const cartGiftWrappingTax = state.config?.cartGiftWrapping;
  const cartPrintedCardTax = state.config?.cartPrintedCard;
  const totalGiftOptions = data?.totalGiftOptions;

  useEffect(() => {
    const dataEvent = events.on(
      'cart/data',
      (payload) => {
        setData(payload as CartModel);

        const shippingAddress = payload?.addresses?.shipping;
        const isVirtual = payload?.isVirtual;

        if (shippingAddress || isVirtual) {
          setEstimatedTotals(null);
        }

        if (estimatedTotals == null) {
          setEstimatedTotals({
            estimatedTaxTotal: {
              amount: payload?.totalTax?.value,
              currency: payload?.totalTax?.currency,
            },
          });
        }
      },
      { eager: true }
    );

    return () => {
      dataEvent?.off();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setHasQuantityErrors(errors);
  }, [errors]);

  useEffect(() => {
    const shippingEvent = events.on('shipping/estimate', (payload) => {
      const formValues = {
        shippingCountry: payload?.address?.countryCode,
        shippingState: payload?.address?.region,
        shippingStateId: payload?.address?.regionId,
        shippingZip: payload?.address?.postCode,
      };

      const shippingMethods = {
        carrier_code: payload?.shippingMethod?.carrierCode || '',
        method_code: payload?.shippingMethod?.methodCode || '',
      };

      handleEstimateTotals(formValues, shippingMethods);
    });

    return () => {
      shippingEvent?.off();
    };
  }, [handleEstimateTotals]);

  const [isCheckoutInitialized, setIsCheckoutInitialized] = useState(false);

  useEffect(() => {
    const checkoutEvent = events.on('checkout/initialized', (payload) => {
      if (!payload || payload?.type === 'quote') return;
      
      setIsCheckoutInitialized(true);
    });

    return () => {
      checkoutEvent?.off();
    };
  }, []);

  const translations = useText({
    checkout: 'Cart.PriceSummary.checkout',
    free: 'Cart.PriceSummary.total.free',
    orderSummary: 'Cart.PriceSummary.orderSummary',
    taxToBeDetermined: 'Cart.PriceSummary.taxToBeDetermined',
  });

  const checkoutLinkDisabled = data?.hasOutOfStockItems || hasQuantityErrors;

  const handleInitiateCheckout = useCallback((e: MouseEvent) => {
    if (!checkoutLinkDisabled && data) {
      e.preventDefault();
      publishInitiateCheckoutEvent(data, state.locale);
      
      setTimeout(() => {
        const checkoutUrl = routeCheckout?.({ cartId: data.id });
        if (checkoutUrl) {
          window.location.href = checkoutUrl;
        }
      }, ANALYTICS_NAVIGATION_DELAY_MS);
    }
  }, [checkoutLinkDisabled, data, routeCheckout]);

  const shipping =
    !isVirtual && slots?.EstimateShipping ? (
      <Slot
        name="EstimateShipping"
        slot={slots.EstimateShipping}
        key="estimateShippingId"
      />
    ) : undefined;

  if (!Object.keys(data ?? {}).length || data?.totalQuantity === 0) {
    return null;
  }

  const coupons =
    enableCoupons && slots?.Coupons ? (
      <Slot name="Coupons" slot={slots.Coupons} key="couponsId" />
    ) : undefined;

  const giftCards =
    enableGiftCards && slots?.GiftCards ? (
      <Slot name="GiftCards" slot={slots.GiftCards} key="giftCardId" />
    ) : undefined;

  return (
    <OrderSummaryComponent
      {...props}
      data-testid="cart-order-summary"
      heading={<div>{translations.orderSummary}</div>}
      shipping={shipping}
      coupons={coupons}
      giftCards={giftCards}
      loading={handleEstimateLoading}
      updateLineItems={updateLineItems}
      appliedGiftCards={
        !data?.appliedGiftCards?.length
          ? undefined
          : {
              label: (
                <Text
                  id="Cart.PriceSummary.giftCard.appliedGiftCards.label"
                  plural={data?.appliedGiftCards?.length}
                  fields={{
                    count: data?.appliedGiftCards?.length,
                  }}
                />
              ),
              price: (
                <Price
                  className="cart-order-summary__price"
                  amount={-calculateTotalGiftCardAmount()}
                  currency={
                    data?.appliedGiftCards?.[0]?.appliedBalance.currency
                  }
                />
              ),
              content: data?.appliedGiftCards
                ?.map(appliedGiftCardsPriceConfig)
                .filter(Boolean) as VNode[],
            }
      }
      printedCard={{
        renderContent: !!totalGiftOptions?.printedCard?.value,
        taxIncluded: cartPrintedCardTax === 'INCLUDING_TAX',
        taxInclAndExcl: cartPrintedCardTax === 'INCLUDING_EXCLUDING_TAX',
        priceExclTax: (
          <Price
            data-testid="printed-card"
            amount={totalGiftOptions?.printedCard?.value}
            currency={totalGiftOptions?.printedCard?.currency}
          />
        ),
        priceInclTax: (
          <Price
            amount={totalGiftOptions?.printedCardInclTax?.value}
            currency={totalGiftOptions?.printedCardInclTax?.currency}
          />
        ),
      }}
      itemsGiftWrapping={{
        renderContent: !!totalGiftOptions?.giftWrappingForItems?.value,
        taxIncluded: cartGiftWrappingTax === 'INCLUDING_TAX',
        taxInclAndExcl: cartGiftWrappingTax === 'INCLUDING_EXCLUDING_TAX',
        priceExclTax: (
          <Price
            amount={totalGiftOptions?.giftWrappingForItems?.value}
            currency={totalGiftOptions?.giftWrappingForItems?.currency}
          />
        ),
        priceInclTax: (
          <Price
            amount={totalGiftOptions?.giftWrappingForItemsInclTax?.value}
            currency={totalGiftOptions?.giftWrappingForItemsInclTax?.currency}
          />
        ),
      }}
      orderGiftWrapping={{
        renderContent: !!totalGiftOptions?.giftWrappingForOrder?.value,
        taxIncluded: cartGiftWrappingTax === 'INCLUDING_TAX',
        taxInclAndExcl: cartGiftWrappingTax === 'INCLUDING_EXCLUDING_TAX',
        priceExclTax: (
          <Price
            amount={totalGiftOptions?.giftWrappingForOrder?.value}
            currency={totalGiftOptions?.giftWrappingForOrder?.currency}
          />
        ),
        priceInclTax: (
          <Price
            amount={totalGiftOptions?.giftWrappingForOrderInclTax?.value}
            currency={totalGiftOptions?.giftWrappingForOrderInclTax?.currency}
          />
        ),
      }}
      subTotal={{
        taxIncluded: isIncludingTax && !isZeroTax,
        taxExcluded: isIncludingAndExcludingTax,
        zeroTaxSubtotal: isZeroTax,
        priceExcludingTax: estimatedTotals?.estimatedSubTotal?.excludingTax ? (
          <Price
            data-testid="subtotal"
            {...estimatedTotals?.estimatedSubTotal?.excludingTax}
          />
        ) : (
          <Price
            data-testid="subtotal"
            amount={data?.subtotal?.excludingTax?.value}
            currency={data?.subtotal?.excludingTax?.currency}
          />
        ),
        price:
          (!isZeroTax && isIncludingTax) ||
          (!isZeroTax && isIncludingAndExcludingTax) ? (
            estimatedTotals?.estimatedSubTotal?.includingTax ? (
              <Price
                data-testid="subtotal"
                {...estimatedTotals?.estimatedSubTotal?.includingTax}
              />
            ) : (
              <Price
                data-testid="subtotal"
                amount={data?.subtotal.includingTax?.value}
                currency={data?.subtotal.includingTax?.currency}
              />
            )
          ) : (
            <Price
              data-testid="subtotal"
              amount={data?.subtotal?.excludingTax?.value}
              currency={data?.subtotal?.excludingTax?.currency}
            />
          ),
      }}
      discounts={data?.appliedDiscounts?.map((discount) => {
        return {
          label: discount.label,
          price: (
            <Price
              data-testid="summary-discount-total"
              amount={-discount.amount.value}
              currency={discount.amount.currency}
              sale={true}
            />
          ),
          coupon: discount?.coupon ? (
            <span>
              <Icon source={Coupon} size="16" />
              {discount?.coupon?.code}
            </span>
          ) : undefined,
        };
      })}
      taxTotal={
        (isVirtual && !data?.appliedTaxes.length && !isCheckoutInitialized) ||
        (estimatedTotals && estimatedTotals.estimatedTaxTotal == null)
          ? {
              price: (
                <span data-testid="tax-total-tbd">
                  {translations.taxToBeDetermined}
                </span>
              ),
            }
          : {
              price: estimatedTotals?.estimatedTaxTotal ? (
                <Price
                  data-testid="tax-total-estimated"
                  {...estimatedTotals?.estimatedTaxTotal}
                />
              ) : (
                <Price
                  data-testid="tax-total-actual"
                  amount={data?.totalTax?.value}
                  currency={data?.totalTax?.currency}
                />
              ),
              estimated:
                (!estimatedTotals || !estimatedTotals.estimatedTaxTotal) &&
                !data?.addresses?.shipping,
            }
      }
      taxesApplied={
        isVirtual
          ? undefined
          : cartTaxesConfig?.fullSummary
          ? (
              estimatedTotals?.estimatedAppliedTaxes?.taxes ||
              data?.appliedTaxes
            )?.map((tax: any) => ({
              label: tax.label,
              price: (
                <Price
                  data-testid="applied-taxes"
                  amount={tax.amount.value}
                  currency={tax.amount.currency}
                />
              ),
            }))
          : undefined
      }
      total={{
        price: estimatedTotals?.estimatedGrandTotalPrice ? (
          estimatedTotals?.estimatedGrandTotalPrice?.includingTax?.amount ===
          0 ? (
            <span data-testid="total-including-tax">{translations.free}</span>
          ) : (
            <Price
              data-testid="total-including-tax-estimated"
              {...estimatedTotals?.estimatedGrandTotalPrice?.includingTax}
            />
          )
        ) : data?.total?.includingTax.value === 0 ? (
          <span data-testid="total-including-tax">{translations.free}</span>
        ) : (
          <Price
            data-testid="total-including-tax-actual"
            amount={data?.total?.includingTax.value}
            currency={data?.total?.includingTax.currency}
          />
        ),
        estimated:
          (!estimatedTotals || !!estimatedTotals?.estimatedTaxTotal) &&
          !data?.addresses?.shipping,
        priceWithoutTax: cartTaxesConfig?.grandTotal ? (
          estimatedTotals?.estimatedAppliedTaxes ? (
            estimatedTotals?.estimatedGrandTotalPrice?.excludingTax?.amount ===
            0 ? (
              <span data-testid="total-excluding-tax">{translations.free}</span>
            ) : (
              <Price
                data-testid="total-excluding-tax"
                {...estimatedTotals?.estimatedGrandTotalPrice?.excludingTax}
              />
            )
          ) : data?.total?.excludingTax.value === 0 ? (
            <span data-testid="total-excluding-tax">{translations.free}</span>
          ) : (
            <Price
              data-testid="total-excluding-tax"
              amount={data?.total?.excludingTax.value}
              currency={data?.total?.excludingTax.currency}
            />
          )
        ) : undefined,
      }}
      primaryAction={
        routeCheckout && (
          <Button
            data-testid="checkout-button"
            variant="primary"
            disabled={checkoutLinkDisabled}
            aria-disabled={checkoutLinkDisabled}
            href={
              checkoutLinkDisabled
                ? undefined
                : routeCheckout({
                    cartId: data!.id,
                  })
            }
            onClick={handleInitiateCheckout}
          >
            {translations.checkout}
          </Button>
        )
      }
      totalSaved={
        showTotalSaved ? (
          <Price
            amount={data?.discount?.value}
            currency={data?.total?.includingTax.currency}
          />
        ) : undefined
      }
    />
  );
};

OrderSummary.getInitialData = async function () {
  return getPersistedCartData();
};
